import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Table,
  Row,
  Col,
  Form,
  Spinner,
  Alert,
  Image,
  Badge,
  Container,
  Card,
  Modal,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";

// Google Maps libraries configuration
const libraries = ["places"];

// Helper function to extract coordinates from backend user object only
const getUserCoordinates = (user) => {
  const rawCoordinates =
    user?.location?.coordinates ?? user?.coordinates ?? null;

  if (Array.isArray(rawCoordinates) && rawCoordinates.length >= 2) {
    const longitude = Number(rawCoordinates[0]);
    const latitude = Number(rawCoordinates[1]);

    if (Number.isFinite(longitude) && Number.isFinite(latitude)) {
      return { longitude, latitude };
    }
  }

  return null;
};

// Helper function to get address from coordinates using reverse geocoding
const getAddressFromCoordinates = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=jsonv2&zoom=18&addressdetails=1&accept-language=en`,
      {
        headers: {
          "User-Agent": "VarahiApp/1.0",
          "Accept-Language": "en",
        },
      }
    );

    if (response.ok) {
      const data = await response.json();

      if (data && data.display_name) {
        let address = data.display_name;

        if (data.address) {
          const parts = [];
          if (data.address.house_number) parts.push(data.address.house_number);
          if (data.address.road) parts.push(data.address.road);
          if (data.address.neighbourhood)
            parts.push(data.address.neighbourhood);
          if (data.address.suburb) parts.push(data.address.suburb);
          if (data.address.city || data.address.town || data.address.village) {
            parts.push(
              data.address.city || data.address.town || data.address.village
            );
          }
          if (data.address.state) parts.push(data.address.state);
          if (data.address.country) parts.push(data.address.country);

          if (parts.length > 0) {
            address = parts.join(", ");
          }
        }

        if (address && !address.toLowerCase().includes("undefined")) {
          return address;
        }
      }
    }
  } catch (error) {
    console.error("Error fetching from Nominatim:", error);
  }

  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};

// Function to get a simplified address from coordinates
const getSimplifiedAddress = async (latitude, longitude) => {
  try {
    const address = await getAddressFromCoordinates(latitude, longitude);

    if (address && address.length > 100) {
      const parts = address.split(",").filter((p) => p.trim());
      const cleanedParts = parts.slice(0, 4);
      return cleanedParts.join(", ");
    }

    return address || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  } catch (error) {
    console.error("Error getting simplified address:", error);
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  }
};

const UsersNerest = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ✅ Get name and number from URL
  const nameParam = searchParams.get("name") || searchParams.get("userName") || "";
  const mobileParam = searchParams.get("number") || searchParams.get("userMobile") || "";

  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [locationInput, setLocationInput] = useState({
    longitude: "",
    latitude: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterField, setFilterField] = useState("name");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [userContacts, setUserContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectedUserMeta, setSelectedUserMeta] = useState({
    name: "",
    mobile: "",
  });
  const [userLocation, setUserLocation] = useState(null);
  const [userAddresses, setUserAddresses] = useState({});
  const [selectedUserAddress, setSelectedUserAddress] = useState("");
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [userLocationAddress, setUserLocationAddress] = useState("");

  // Debug state
  const [debugInfo, setDebugInfo] = useState({
    lastFetchTime: null,
    lastLat: null,
    lastLng: null,
    changesDetected: 0,
    lastChangeTime: null,
    isPolling: false,
  });

  // ✅ Refs for location tracking
  const pollingTimeoutRef = useRef(null);
  const previousLocationRef = useRef(null);
  const isPollingActiveRef = useRef(false);
  const mapRef = useRef(null);
  const isMountedRef = useRef(true);
  const pollingRunIdRef = useRef(0);
  const pollingAbortControllerRef = useRef(null);
  const trackingUserRef = useRef({ name: "", mobile: "" });

  // Google Maps API Key
  const googleMapsApiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: googleMapsApiKey,
    libraries: libraries,
  });

  // ✅ FETCH USER LOCATION BY NAME AND NUMBER
  const fetchUserLocationByNameNumber = async (name, number, runId, signal) => {
    if (
      !isMountedRef.current ||
      (!name && !number) ||
      trackingUserRef.current.name !== name ||
      trackingUserRef.current.mobile !== number ||
      pollingRunIdRef.current !== runId
    ) {
      console.log("⏸️ Skipping fetch - conditions not met");
      return;
    }

    try {
      const timestamp = new Date().toLocaleTimeString();
      console.log(`🔄 [${timestamp}] Fetching location for: name=${name}, number=${number}`);

      const params = new URLSearchParams({
        hasLocation: "true",
        geoSearch: "true",
      });

      if (name && name.trim()) {
        params.append("name", name.trim());
      }

      if (number && number.trim()) {
        params.append("number", number.trim());
      }

      const url = `https://varahibackend.varahiselfdrivecars.com/api/users/all-users-location?${params.toString()}`;
      console.log(`📡 [${timestamp}] URL:`, url);

      const response = await fetch(url, { signal });

      if (!response.ok) {
        console.error(`❌ [${timestamp}] Failed to fetch:`, response.status);
        return;
      }

      const data = await response.json();

      // ✅ Check if this response is still valid
      if (
        !isMountedRef.current ||
        trackingUserRef.current.name !== name ||
        trackingUserRef.current.mobile !== number ||
        pollingRunIdRef.current !== runId
      ) {
        console.log("⏸️ Response discarded - stale request");
        return;
      }

      const usersList = Array.isArray(data.users) ? data.users : [];

      if (usersList.length === 0) {
        console.log(`❌ [${timestamp}] No users found`);
        return;
      }

      // ✅ Find exact match by name AND number
      const matchedUser = usersList.find((user) => {
        const userName = user.name || "";
        const userNumber = user.mobile || "";
        const nameMatch = name ? userName.toLowerCase().includes(name.toLowerCase()) : true;
        const numberMatch = number ? userNumber === number : true;
        return nameMatch && numberMatch;
      });

      if (!matchedUser) {
        console.log(`❌ [${timestamp}] No exact match found`);
        return;
      }

      const coords = getUserCoordinates(matchedUser);
      if (!coords) {
        console.log(`❌ [${timestamp}] No coordinates found`);
        return;
      }

      const { latitude, longitude } = coords;

      // ✅ Double-check validity before updating state
      if (
        !isMountedRef.current ||
        trackingUserRef.current.name !== name ||
        trackingUserRef.current.mobile !== number ||
        pollingRunIdRef.current !== runId
      ) {
        return;
      }

      setDebugInfo(prev => ({
        ...prev,
        lastFetchTime: timestamp,
        lastLat: latitude,
        lastLng: longitude,
        isPolling: true,
      }));

      console.log(`📍 [${timestamp}] Fetched coords: lat=${latitude}, lng=${longitude}`);

      // ✅ Check if location has changed
      const hasLocationChanged = () => {
        if (!previousLocationRef.current) {
          console.log(`🆕 [${timestamp}] First location set`);
          return true;
        }

        const prev = previousLocationRef.current;
        const latDiff = Math.abs(latitude - prev.latitude);
        const lngDiff = Math.abs(longitude - prev.longitude);

        const threshold = 0.00009;
        const changed = latDiff > threshold || lngDiff > threshold;

        console.log(`📊 [${timestamp}] Comparison:`);
        console.log(`   Previous: lat=${prev.latitude}, lng=${prev.longitude}`);
        console.log(`   Current:  lat=${latitude}, lng=${longitude}`);
        console.log(`   Changed: ${changed ? '✅ YES' : '❌ NO'}`);

        return changed;
      };

      if (hasLocationChanged()) {
        console.log(`✅ [${timestamp}] 🔴 LOCATION CHANGED! Updating map... 🔴`);

        if (!isMountedRef.current) return;

        setDebugInfo(prev => ({
          ...prev,
          changesDetected: prev.changesDetected + 1,
          lastChangeTime: timestamp,
        }));

        setUserLocation({ latitude, longitude });
        previousLocationRef.current = { latitude, longitude };

        // ✅ Update selectedUser if exists
        setSelectedUser((prevUser) => {
          if (!prevUser) return matchedUser;
          return {
            ...prevUser,
            location: {
              ...prevUser.location,
              coordinates: [longitude, latitude],
            },
          };
        });

        // ✅ Update user meta
        setSelectedUserMeta({
          name: matchedUser.name || name || "",
          mobile: matchedUser.mobile || number || "",
        });

        // ✅ Update address
        try {
          const address = await getAddressFromCoordinates(latitude, longitude);
          if (!isMountedRef.current ||
            trackingUserRef.current.name !== name ||
            trackingUserRef.current.mobile !== number ||
            pollingRunIdRef.current !== runId) {
            return;
          }
          setUserLocationAddress(address || "Address not available");
          console.log(`📍 [${timestamp}] Address: ${address}`);
        } catch (err) {
          if (!isMountedRef.current) return;
          setUserLocationAddress(
            `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
          );
        }

        // ✅ Auto-pan map
        if (mapRef.current) {
          const position = { lat: latitude, lng: longitude };
          console.log(`🗺️ [${timestamp}] Panning map to:`, position);
          mapRef.current.panTo(position);
          mapRef.current.setZoom(20);
        }
      } else {
        console.log(`⏸️ [${timestamp}] Location unchanged`);
      }

      console.log(`═══════════════════════════════════════════`);

    } catch (error) {
      if (error.name === "AbortError") {
        console.log("⏸️ Fetch aborted");
        return;
      }
      console.error(`❌ Error fetching location:`, error);
    }
  };

  // ✅ Stop polling
  const stopPolling = () => {
    console.log("🛑 Stopping polling...");
    pollingRunIdRef.current += 1;

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    if (pollingAbortControllerRef.current) {
      pollingAbortControllerRef.current.abort();
      pollingAbortControllerRef.current = null;
    }

    isPollingActiveRef.current = false;
    trackingUserRef.current = { name: "", mobile: "" };

    if (isMountedRef.current) {
      setDebugInfo((prev) => ({ ...prev, isPolling: false }));
    }
  };

  // ✅ Start polling with name and number
  const startPolling = (name, number) => {
    stopPolling();

    if ((!name && !number) || !isMountedRef.current) {
      console.log("⏸️ Cannot start polling - no name/number or component unmounted");
      return;
    }

    const runId = pollingRunIdRef.current + 1;
    pollingRunIdRef.current = runId;
    trackingUserRef.current = { name: name || "", mobile: number || "" };
    isPollingActiveRef.current = true;

    setDebugInfo((prev) => ({ ...prev, isPolling: true }));

    console.log(`🔄🔁 STARTING POLLING for: name=${name}, number=${number}`);
    console.log(`⏱️ Polling interval: 1 SECOND`);

    // ✅ Recursive poll function with 1-second delay
    const poll = async () => {
      if (
        !isMountedRef.current ||
        pollingRunIdRef.current !== runId ||
        trackingUserRef.current.name !== (name || "") ||
        trackingUserRef.current.mobile !== (number || "")
      ) {
        console.log("⏸️ Polling stopped - conditions changed");
        return;
      }

      const controller = new AbortController();
      pollingAbortControllerRef.current = controller;

      await fetchUserLocationByNameNumber(name, number, runId, controller.signal);

      if (pollingAbortControllerRef.current === controller) {
        pollingAbortControllerRef.current = null;
      }

      // ✅ Schedule next poll after 1 SECOND
      if (
        isMountedRef.current &&
        pollingRunIdRef.current === runId &&
        trackingUserRef.current.name === (name || "") &&
        trackingUserRef.current.mobile === (number || "")
      ) {
        pollingTimeoutRef.current = setTimeout(poll, 1000); // ⬅️ 1 SECOND
      }
    };

    poll(); // Immediate first call
  };

  // ✅ Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      console.log("🗑️ Component unmounting - cleaning up polling");
      isMountedRef.current = false;
      stopPolling();
    };
  }, []);

  // ✅ MAIN EFFECT: Read name/number from URL and start polling
  useEffect(() => {
    const latParam = searchParams.get("latitude");
    const lngParam = searchParams.get("longitude");
    const name = searchParams.get("name") || searchParams.get("userName") || "";
    const number = searchParams.get("number") || searchParams.get("userMobile") || "";

    console.log("📋 URL Params received:", {
      latParam,
      lngParam,
      name,
      number,
      decodedName: decodeURIComponent(name),
      decodedNumber: decodeURIComponent(number),
    });

    // ✅ Set user meta from URL
    if (name || number) {
      setSelectedUserMeta({
        name: decodeURIComponent(name) || "",
        mobile: decodeURIComponent(number) || "",
      });
    } else {
      setSelectedUserMeta({ name: "", mobile: "" });
    }

    // ✅ Start polling based on name AND/OR number
    if (name || number) {
      console.log(`👤 Starting polling for: name=${name}, number=${number}`);
      startPolling(name, number);
    } else {
      console.log("⏸️ No name/number in URL, stopping polling");
      stopPolling();
      setSelectedUser(null);
      setUserLocation(null);
      setUserLocationAddress("");
    }

    // ✅ Fetch nearby users if coordinates are present
    if (latParam && lngParam) {
      const parsedLat = parseFloat(latParam);
      const parsedLng = parseFloat(lngParam);

      if (!Number.isNaN(parsedLat) && !Number.isNaN(parsedLng)) {
        setLocationInput({
          longitude: String(parsedLng),
          latitude: String(parsedLat),
        });

        setUserLocation(null);
        setUserLocationAddress("");

        const decodedName = decodeURIComponent(name);
        const decodedNumber = decodeURIComponent(number);

        fetchNearbyUsers(parsedLng, parsedLat, decodedName, decodedNumber);
      }
    }
  }, [searchParams]); // ✅ Runs on URL change

  // ✅ Fetch nearby users (existing function - keep as is)
  const fetchNearbyUsers = async (
    longitude,
    latitude,
    name = "",
    number = ""
  ) => {
    try {
      setLoading(true);
      setError("");
      setNearbyUsers([]);
      setUserLocation(null);
      setUserLocationAddress("");
      setUserAddresses({});
      setAddressesLoading(true);

      const params = new URLSearchParams({
        hasLocation: "true",
        geoSearch: "true",
        location: `${longitude},${latitude}`,
      });

      if (name && name.trim()) {
        params.append("name", name.trim());
      }

      if (number && number.trim()) {
        params.append("number", number.trim());
      }

      const url = `https://varahibackend.varahiselfdrivecars.com/api/users/all-users-location?${params.toString()}`;
      console.log("📡 Fetching nearby users with URL:", url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch nearby users");
      }

      const data = await response.json();
      console.log("📦 API Response:", data);

      const usersList = Array.isArray(data.users) ? data.users : [];

      if (usersList.length === 0) {
        setNearbyUsers([]);
        setUserLocation(null);
        setUserLocationAddress("");
        toast.error("User location cannot be found");
        return;
      }

      const usersWithCoordinates = usersList.filter((user) => {
        const coords = getUserCoordinates(user);
        console.log("📍 Backend user location:", {
          userId: user.userId || user.id,
          name: user.name,
          coordinates: user.location?.coordinates || user.coordinates,
        });
        return !!coords;
      });

      if (usersWithCoordinates.length === 0) {
        setNearbyUsers([]);
        setUserLocation(null);
        setUserLocationAddress("");
        toast.error("User location cannot be found");
        return;
      }

      setNearbyUsers(usersWithCoordinates);

      const addressPromises = usersWithCoordinates.map(async (user) => {
        const coords = getUserCoordinates(user);
        if (!coords) return { userId: user.userId, address: null };

        const lat = coords.latitude;
        const lng = coords.longitude;

        try {
          const address = await getSimplifiedAddress(lat, lng);
          return { userId: user.userId, address };
        } catch (err) {
          console.error(`Error fetching address for user ${user.userId}:`, err);
          return {
            userId: user.userId,
            address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          };
        }
      });

      const addressResults = await Promise.all(addressPromises);
      const addressMap = {};
      addressResults.forEach(({ userId, address }) => {
        if (address) {
          addressMap[userId] = address;
        }
      });
      setUserAddresses(addressMap);

      const actualUser = usersWithCoordinates[0];
      const actualCoords = getUserCoordinates(actualUser);
      if (actualCoords) {
        const { latitude: actualLatitude, longitude: actualLongitude } =
          actualCoords;
        setUserLocation({
          latitude: actualLatitude,
          longitude: actualLongitude,
        });
        previousLocationRef.current = {
          latitude: actualLatitude,
          longitude: actualLongitude,
        };
        console.log("🗺️ Map using actual backend user coordinates:", {
          latitude: actualLatitude,
          longitude: actualLongitude,
        });

        try {
          const locationAddress = await getAddressFromCoordinates(
            actualLatitude,
            actualLongitude
          );
          setUserLocationAddress(locationAddress || "Address not available");
        } catch (err) {
          setUserLocationAddress(
            `${actualLatitude.toFixed(6)}, ${actualLongitude.toFixed(6)}`
          );
        }
      }

      toast.success(`Found ${usersWithCoordinates.length} nearby users`);
    } catch (err) {
      console.error("❌ Error fetching nearby users:", err);
      setError(err.message || "Failed to fetch nearby users");
      setNearbyUsers([]);
      setUserLocation(null);
      setUserLocationAddress("");
      toast.error("User location cannot be found");
    } finally {
      setLoading(false);
      setAddressesLoading(false);
    }
  };

  // ✅ Keep rest of the component (handlers, renderMap, return, etc.) same as before
  // ... (All existing handlers, renderMap, and return JSX remain unchanged) ...

  // Filter users based on search
  const getFilteredUsers = () => {
    if (!searchTerm) return nearbyUsers;

    return nearbyUsers.filter((user) => {
      const value = user[filterField]
        ? user[filterField].toString().toLowerCase()
        : "";
      return value.includes(searchTerm.toLowerCase());
    });
  };

  // Sort users
  const getSortedUsers = () => {
    const filtered = getFilteredUsers();
    if (!sortConfig.key) return filtered;

    return [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || "";
      const bVal = b[sortConfig.key] || "";

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleViewDetails = async (userId) => {
    try {
      console.log(`👤 VIEWING DETAILS for user: ${userId}`);
      console.log(`═══════════════════════════════════════════`);
      setContactsLoading(true);
      setUserContacts([]);
      setSelectedUserAddress("");
      setUserLocation(null);
      setUserLocationAddress("");

      const res = await fetch(
        `https://varahibackend.varahiselfdrivecars.com/api/users/get-user/${userId}`
      );
      if (!res.ok) throw new Error("Failed to fetch user details");
      const data = await res.json();
      const user = data.user;

      console.log("👤 User data received:", {
        name: user.name,
        mobile: user.mobile,
        coordinates: user.location?.coordinates || user.coordinates,
      });

      const userCoords = getUserCoordinates(user);

      setSelectedUser(user);
      console.log(`✅ Selected user set: ${user.name} (${user.userId || user.id})`);

      if (userCoords) {
        const longitude = userCoords.longitude;
        const latitude = userCoords.latitude;

        console.log(`📍 INITIAL LOCATION: lat=${latitude}, lng=${longitude}`);

        setUserLocation({ latitude, longitude });
        previousLocationRef.current = { latitude, longitude };

        try {
          const address = await getAddressFromCoordinates(latitude, longitude);
          setSelectedUserAddress(address || "Address not available");
          setUserLocationAddress(address || "Address not available");
        } catch (err) {
          const fallback = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          setSelectedUserAddress(fallback);
          setUserLocationAddress(fallback);
        }
      } else {
        console.log("⚠️ No coordinates found for user");
        setUserLocation(null);
        setUserLocationAddress("");
        toast.error("User location cannot be found");
      }

      setShowUserDetails(true);
      console.log(`═══════════════════════════════════════════`);
    } catch (err) {
      console.error("❌ Error in handleViewDetails:", err);
      toast.error(err.message || "User location cannot be found");
    } finally {
      setContactsLoading(false);
    }
  };

  const exportToExcel = () => {
    if (nearbyUsers.length === 0) {
      toast.warning("No users to export");
      return;
    }

    const data = nearbyUsers.map((user, i) => ({
      "S.No": i + 1,
      Name: user.name || "-",
      Mobile: user.mobile || "-",
      Email: user.email || "-",
      Address: userAddresses[user.userId] || "Address not available",
      Latitude: user.coordinates?.[1]?.toFixed(6) || "-",
      Longitude: user.coordinates?.[0]?.toFixed(6) || "-",
      "Distance (km)": user.distance?.toFixed(1) || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Nearby Users");
    XLSX.writeFile(wb, "nearby_users.xlsx");
    toast.success("Excel file downloaded successfully!");
  };

  const handleFindUsers = () => {
    const long = parseFloat(locationInput.longitude);
    const lat = parseFloat(locationInput.latitude);

    if (isNaN(long) || isNaN(lat)) {
      toast.error("Please enter valid longitude and latitude");
      return;
    }

    setUserLocation(null);
    setUserLocationAddress("");
    setSelectedUser(null);
    fetchNearbyUsers(long, lat);
  };

  const getStatusBadge = (status) => {
    if (!status) return "secondary";
    switch (status.toLowerCase()) {
      case "pending":
        return "warning";
      case "confirmed":
        return "info";
      case "active":
        return "primary";
      case "completed":
        return "success";
      case "cancelled":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getPaymentBadge = (paymentStatus) => {
    if (!paymentStatus) return "secondary";
    switch (paymentStatus.toLowerCase()) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      default:
        return "secondary";
    }
  };

  const sortedUsers = getSortedUsers();

  // Determine map center
  const getMapCenter = () => {
    if (userLocation && userLocation.latitude && userLocation.longitude) {
      return {
        lat: userLocation.latitude,
        lng: userLocation.longitude,
      };
    }
    return null;
  };

  // Auto-center map
  useEffect(() => {
    if (!userLocation || !mapRef.current) return;

    const latitude = Number(userLocation.latitude);
    const longitude = Number(userLocation.longitude);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    const position = {
      lat: latitude,
      lng: longitude,
    };

    console.log("🗺️ Auto-centering map to:", position);
    mapRef.current.panTo(position);
    mapRef.current.setZoom(20);
  }, [userLocation]);

  // Render map content
  const renderMap = () => {
    if (!googleMapsApiKey) {
      return (
        <Alert variant="danger" className="text-center m-3">
          <i className="fas fa-exclamation-circle me-2"></i>
          Missing Google Maps API Key. Please add to .env file.
        </Alert>
      );
    }

    if (loadError) {
      return (
        <Alert variant="danger" className="text-center m-3">
          <i className="fas fa-exclamation-circle me-2"></i>
          Error loading Google Maps. Please check your API key.
        </Alert>
      );
    }

    if (!isLoaded) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading Google Maps...</p>
        </div>
      );
    }

    const center = getMapCenter();

    if (!center) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading current location...</p>
        </div>
      );
    }

    return (
      <GoogleMap
        mapContainerStyle={{
          width: "100%",
          height: "450px",
        }}
        center={center}
        zoom={17}
        onLoad={(map) => {
          console.log("🗺️ Map loaded successfully");
          mapRef.current = map;
        }}
        onUnmount={() => {
          console.log("🗺️ Map unmounted");
          mapRef.current = null;
        }}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          mapTypeId: "roadmap",
        }}
      >
        {userLocation && userLocation.latitude && userLocation.longitude && (
          <MarkerF
            position={{
              lat: userLocation.latitude,
              lng: userLocation.longitude,
            }}
            title="Current User Location"
            icon={{
              url: "/user-location.png",
              scaledSize: new window.google.maps.Size(50, 50),
              anchor: new window.google.maps.Point(25, 50),
            }}
          />
        )}
      </GoogleMap>
    );
  };

  return (
    <Container
      fluid
      className="p-4"
      style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
    >
      <ToastContainer position="top-right" autoClose={2000} />

      {/* Header */}
      <div className="mb-5">
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h1
              className="mb-2"
              style={{
                fontSize: "2.5rem",
                fontWeight: "700",
                color: "#1f2937",
              }}
            >
              <i className="fas fa-map-location-dot me-3 text-primary"></i>
              Nearby Users
            </h1>
            <p className="text-muted mb-0" style={{ fontSize: "1.05rem" }}>
              <i className="fas fa-info-circle me-2"></i>
              Search and locate users based on geographical coordinates
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={() => navigate("/admin/users")}
              className="d-flex align-items-center"
              style={{ fontWeight: "600", fontSize: "0.95rem" }}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to Users
            </Button>
            <Button
              variant="success"
              onClick={exportToExcel}
              disabled={nearbyUsers.length === 0}
              className="d-flex align-items-center"
              style={{ fontWeight: "600", fontSize: "0.95rem" }}
            >
              <i className="fas fa-file-excel me-2"></i>
              Export to Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Location Input Section */}
      <Card
        className="mb-4 border-0 shadow-sm"
        style={{ borderRadius: "12px" }}
      >
        <Card.Header
          className="bg-white border-bottom"
          style={{ borderRadius: "12px 12px 0 0", padding: "1.5rem" }}
        >
          <h5 className="mb-0" style={{ fontWeight: "700", color: "#1f2937" }}>
            <i className="fas fa-location-crosshairs me-2 text-primary"></i>
            Search Location
          </h5>
        </Card.Header>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label
                  className="fw-600"
                  style={{ color: "#374151", marginBottom: "0.5rem" }}
                >
                  <i className="fas fa-compass me-2 text-primary"></i>
                  Longitude
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 78.4867"
                  value={locationInput.longitude}
                  onChange={(e) =>
                    setLocationInput({
                      ...locationInput,
                      longitude: e.target.value,
                    })
                  }
                  className="rounded-lg"
                  style={{ borderRadius: "8px", padding: "0.75rem" }}
                />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label
                  className="fw-600"
                  style={{ color: "#374151", marginBottom: "0.5rem" }}
                >
                  <i className="fas fa-compass me-2 text-primary"></i>
                  Latitude
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 17.3850"
                  value={locationInput.latitude}
                  onChange={(e) =>
                    setLocationInput({
                      ...locationInput,
                      latitude: e.target.value,
                    })
                  }
                  className="rounded-lg"
                  style={{ borderRadius: "8px", padding: "0.75rem" }}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button
                variant="primary"
                onClick={handleFindUsers}
                className="w-100 fw-600"
                style={{
                  padding: "0.75rem",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                }}
              >
                <i className="fas fa-search me-2"></i>
                Find
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Debug Info Panel */}
      {debugInfo.isPolling && (
        <Card className="mb-3 border border-info shadow-sm" style={{ borderRadius: "8px" }}>
          <Card.Header className="bg-info text-white py-2">
            <h6 className="mb-0">
              <i className="fas fa-bug me-2"></i>
              Live Location Debug Console
            </h6>
          </Card.Header>
          <Card.Body className="py-2 px-3" style={{ fontSize: "0.85rem", backgroundColor: "#f8f9fa" }}>
            <Row>
              <Col md={3}>
                <strong>Status:</strong>
                <Badge bg={debugInfo.isPolling ? "success" : "secondary"} className="ms-2">
                  {debugInfo.isPolling ? "🔄 Polling Active" : "⏸️ Paused"}
                </Badge>
              </Col>
              <Col md={3}>
                <strong>Last Fetch:</strong> {debugInfo.lastFetchTime || "—"}
              </Col>
              <Col md={3}>
                <strong>Current Location:</strong>
                {debugInfo.lastLat && debugInfo.lastLng ? (
                  <span className="ms-1">
                    <Badge bg="primary">Lat: {debugInfo.lastLat.toFixed(6)}</Badge>
                    <Badge bg="primary" className="ms-1">Lng: {debugInfo.lastLng.toFixed(6)}</Badge>
                  </span>
                ) : " —"}
              </Col>
              <Col md={3}>
                <strong>Changes Detected:</strong>
                <Badge bg="danger" className="ms-2">
                  {debugInfo.changesDetected}
                </Badge>
                {debugInfo.lastChangeTime && (
                  <span className="ms-1 text-muted small">(at {debugInfo.lastChangeTime})</span>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Selected User Info Badge */}
      {(selectedUserMeta.name || selectedUserMeta.mobile) && (
        <div className="mb-3">
          <Badge bg="primary" className="p-2 fs-6">
            <i className="fas fa-user me-2"></i>
            Tracking: {selectedUserMeta.name || "User"}
            {selectedUserMeta.mobile ? ` (${selectedUserMeta.mobile})` : ""}
          </Badge>
        </div>
      )}

      {!loading && nearbyUsers.length > 0 && (
        <div className="mb-3">
          <Badge bg="info" className="p-2 fs-6">
            <i className="fas fa-users me-2"></i>
            Found {nearbyUsers.length} nearby users
          </Badge>
        </div>
      )}

      {/* Google Map Section */}
      {(userLocation || nearbyUsers.length > 0 || loading) && (
        <Card
          className="mb-4 border-0 shadow-sm"
          style={{ borderRadius: "12px", overflow: "hidden" }}
        >
          <Card.Header
            className="text-white"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "1.5rem",
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5
                  className="mb-1"
                  style={{ fontWeight: "700", fontSize: "1.2rem" }}
                >
                  <i className="fas fa-map me-2"></i>
                  Live Location
                </h5>
                <small
                  className="text-white-50 d-block"
                  style={{ fontSize: "0.9rem" }}
                >
                  {selectedUser?.name || selectedUserMeta.name || "User"}
                  {selectedUser?.mobile || selectedUserMeta.mobile
                    ? ` • ${selectedUser?.mobile || selectedUserMeta.mobile}`
                    : ""}
                  {selectedUser && (
                    <span className="ms-2">
                      <Badge bg="light" text="dark" className="ms-2">
                        <i className="fas fa-sync me-1"></i>
                        Auto-updating
                      </Badge>
                    </span>
                  )}
                </small>
              </div>
              {userLocation &&
                userLocation.latitude &&
                userLocation.longitude && (
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => {
                      const mapsUrl = `https://www.google.com/maps?q=${userLocation.latitude},${userLocation.longitude}`;
                      window.open(mapsUrl, "_blank");
                    }}
                    style={{ fontWeight: "600", fontSize: "0.85rem" }}
                  >
                    <i className="fas fa-external-link-alt me-1"></i>
                    Open in Maps
                  </Button>
                )}
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading location...</p>
              </div>
            ) : (
              <>
                {renderMap()}
                {userLocationAddress && (
                  <div className="p-3 bg-light border-top">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fas fa-location-dot text-primary"></i>
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.9rem" }}
                      >
                        <strong>Address:</strong> {userLocationAddress}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Alert variant="danger" className="mb-3">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner
            animation="border"
            variant="primary"
            style={{ width: "3rem", height: "3rem" }}
          />
          <p className="mt-3 text-muted">Searching for nearby users...</p>
        </div>
      ) : nearbyUsers.length === 0 ? (
        <Card className="text-center py-5 bg-light">
          <Card.Body>
            <i
              className="fas fa-map-marker-alt text-muted"
              style={{ fontSize: "4rem" }}
            ></i>
            <h5 className="mt-3 text-muted">No nearby users found</h5>
            <p className="text-muted">
              Enter coordinates to find users near the selected location.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* Search and Filter */}
          <Card
            className="mb-4 border-0 shadow-sm"
            style={{ borderRadius: "12px" }}
          >
            <Card.Body className="p-4">
              <Row className="mb-0 g-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label
                      className="fw-600"
                      style={{ color: "#374151", fontSize: "0.9rem" }}
                    >
                      Filter by
                    </Form.Label>
                    <Form.Select
                      value={filterField}
                      onChange={(e) => setFilterField(e.target.value)}
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="name">Search by Name</option>
                      <option value="email">Search by Email</option>
                      <option value="mobile">Search by Mobile</option>
                      <option value="userId">Search by ID</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-600"
                      style={{ color: "#374151", fontSize: "0.9rem" }}
                    >
                      <i className="fas fa-search me-2 text-primary"></i>
                      Search
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={`Search by ${filterField}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Badge
                    bg="light"
                    text="dark"
                    className="p-2 w-100 text-center"
                    style={{ fontSize: "0.95rem", fontWeight: "600" }}
                  >
                    <i className="fas fa-list me-2 text-primary"></i>
                    {sortedUsers.length} / {nearbyUsers.length} Users
                  </Badge>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Users Table */}
          <Card
            className="border-0 shadow-sm"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Card.Header
              style={{
                background: "#f3f4f6",
                padding: "1.5rem",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <h5
                className="mb-0"
                style={{ fontWeight: "700", color: "#1f2937" }}
              >
                <i className="fas fa-users me-2 text-primary"></i>
                Nearby Users List
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table striped bordered hover className="align-middle mb-0">
                  <thead style={{ backgroundColor: "#f9fafb" }}>
                    <tr>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("")}
                      >
                        # <i className="fas fa-sort text-muted"></i>
                      </th>
                      <th>Profile</th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("name")}
                      >
                        Name <i className="fas fa-sort text-muted"></i>
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("mobile")}
                      >
                        Mobile <i className="fas fa-sort text-muted"></i>
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("email")}
                      >
                        Email <i className="fas fa-sort text-muted"></i>
                      </th>
                      <th>Address</th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("distance")}
                      >
                        Distance <i className="fas fa-sort text-muted"></i>
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map((user, index) => (
                      <tr key={user.userId || index}>
                        <td className="text-center">{index + 1}</td>
                        <td>
                          <Image
                            src={
                              user.profileImage ||
                              `https://placehold.co/40x40/0d6efd/ffffff?text=${(user.name?.charAt(0) || "U").toUpperCase()}`
                            }
                            roundedCircle
                            width={40}
                            height={40}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://placehold.co/40x40/0d6efd/ffffff?text=${(user.name?.charAt(0) || "U").toUpperCase()}`;
                            }}
                          />
                        </td>
                        <td>
                          <strong>{user.name || "-"}</strong>
                        </td>
                        <td>{user.mobile || "-"}</td>
                        <td>{user.email || "-"}</td>
                        <td>
                          {addressesLoading ? (
                            <Spinner animation="border" size="sm" />
                          ) : userAddresses[user.userId] ? (
                            <small title={userAddresses[user.userId]}>
                              <i className="fas fa-map-pin text-primary me-1"></i>
                              {userAddresses[user.userId].length > 40
                                ? userAddresses[user.userId].substring(0, 40) +
                                "..."
                                : userAddresses[user.userId]}
                            </small>
                          ) : (
                            <small className="text-muted">
                              <i className="fas fa-map-pin me-1"></i>
                              {(() => {
                                const coords = getUserCoordinates(user);
                                if (coords) {
                                  return (
                                    <>
                                      Lat: {coords.latitude.toFixed(6)}
                                      <br />
                                      Lng: {coords.longitude.toFixed(6)}
                                    </>
                                  );
                                }
                                return "Location unavailable";
                              })()}
                            </small>
                          )}
                        </td>
                        <td>
                          {user.distance ? (
                            <Badge bg="primary">
                              {user.distance.toFixed(1)} km
                            </Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewDetails(user.userId)}
                              title="View Details"
                            >
                              <i className="fas fa-eye"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
          {/* No results after search */}
          {sortedUsers.length === 0 && searchTerm && (
            <Alert variant="info" className="text-center">
              <i className="fas fa-search me-2"></i>
              No users found matching your search criteria
            </Alert>
          )}
        </>
      )}

      {/* User Details Modal */}
      <Modal
        show={showUserDetails}
        onHide={() => {
          setShowUserDetails(false);
          setSelectedUser(null);
          setUserContacts([]);
          setSelectedUserAddress("");
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-primary">User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser ? (
            <>
              <Row className="mb-3">
                <Col md={4} className="text-center">
                  <Image
                    src={selectedUser.profileImage || "/profile.png"}
                    alt="Profile"
                    fluid
                    style={{
                      width: "200px",
                      height: "200px",
                      objectFit: "cover",
                      borderRadius: "50%",
                      border: "2px solid #ccc",
                    }}
                  />
                </Col>
                <Col md={8}>
                  <p>
                    <strong>User ID:</strong> {selectedUser.id}
                  </p>
                  <p>
                    <strong>Name:</strong> {selectedUser.name}
                  </p>
                  <p>
                    <strong>Email:</strong> {selectedUser.email}
                  </p>
                  <p>
                    <strong>Mobile:</strong> {selectedUser.mobile}
                  </p>
                  <p>
                    <strong>Address:</strong>
                    <span className="ms-2">
                      {selectedUserAddress || "Address not available"}
                    </span>
                  </p>
                  <p>
                    <strong>Created At:</strong>{" "}
                    {new Date(selectedUser.createdAt).toLocaleString()}
                  </p>
                </Col>
              </Row>

              <hr />

              <h4 className="text-primary">Contacts ({userContacts.length})</h4>
              {contactsLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <div
                  className="table-responsive mb-4"
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr className="table-header">
                        <th>Contact Name</th>
                        <th>Phone Number</th>
                        <th>Status</th>
                        <th>Added At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userContacts.length > 0 ? (
                        userContacts.map((contact, index) => (
                          <tr key={index}>
                            <td>{contact.contactName || "-"}</td>
                            <td>{contact.contactPhoneNumber || "-"}</td>
                            <td>
                              <Badge
                                bg={
                                  contact.isRegistered ? "success" : "secondary"
                                }
                              >
                                {contact.isRegistered
                                  ? "Registered"
                                  : "Not Registered"}
                              </Badge>
                            </td>
                            <td>
                              {contact.addedAt
                                ? new Date(contact.addedAt).toLocaleString()
                                : "-"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center">
                            No contacts found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              <hr />

              <h4 className="text-primary">Documents</h4>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Aadhar Card:</strong>
                  </p>
                  {selectedUser.documents?.aadharCard?.url ? (
                    <>
                      <Image
                        src={selectedUser.documents.aadharCard.url}
                        style={{ maxWidth: "100%", height: "auto" }}
                        fluid
                        thumbnail
                      />
                      <p>
                        Status:{" "}
                        <Badge
                          bg={
                            selectedUser.documents.aadharCard.status ===
                              "approved"
                              ? "success"
                              : "warning"
                          }
                        >
                          {selectedUser.documents.aadharCard.status}
                        </Badge>
                      </p>
                    </>
                  ) : (
                    <p>Not uploaded</p>
                  )}
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Driving License:</strong>
                  </p>
                  {selectedUser.documents?.drivingLicense?.url ? (
                    <>
                      <Image
                        src={selectedUser.documents.drivingLicense.url}
                        style={{ maxWidth: "100%", height: "auto" }}
                        fluid
                        thumbnail
                      />
                      <p>
                        Status:{" "}
                        <Badge
                          bg={
                            selectedUser.documents.drivingLicense.status ===
                              "approved"
                              ? "success"
                              : "warning"
                          }
                        >
                          {selectedUser.documents.drivingLicense.status}
                        </Badge>
                      </p>
                    </>
                  ) : (
                    <p>Not uploaded</p>
                  )}
                </Col>
              </Row>

              <hr />

              <h4 className="text-primary">
                Bookings ({selectedUser.myBookings?.length || 0})
              </h4>
              <div
                className="table-responsive"
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr className="table-header">
                      <th>Car ID</th>
                      <th>Date</th>
                      <th>Price</th>
                      <th>Payment</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedUser.myBookings?.length > 0 ? (
                      selectedUser.myBookings.map((booking, idx) => (
                        <tr key={idx}>
                          <td>{booking.carId?.slice(-6)}</td>
                          <td>{booking.rentalStartDate}</td>
                          <td>₹{booking.totalPrice}</td>
                          <td>
                            <Badge bg={getPaymentBadge(booking.paymentStatus)}>
                              {booking.paymentStatus || "Unknown"}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={getStatusBadge(booking.status)}>
                              {booking.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No bookings found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowUserDetails(false);
              setSelectedUser(null);
              setUserContacts([]);
              setSelectedUserAddress("");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UsersNerest;